import React, { useEffect, useCallback } from 'react'
import { createPortal } from 'react-dom'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { cn } from '@/utils/cn'

interface ModalProps {
  open: boolean
  title?: string
  onClose: () => void
  onSave?: () => void
  saveLabel?: string
  hideSave?: boolean
  wide?: boolean
  danger?: boolean
  extraActions?: React.ReactNode
  children?: React.ReactNode
  loading?: boolean
  className?: string
  bodyClassName?: string
  noPad?: boolean
}

export function Modal({
  open,
  title,
  onClose,
  onSave,
  saveLabel = 'Save',
  hideSave = false,
  wide = false,
  danger = false,
  extraActions,
  children,
  loading = false,
  className,
  bodyClassName,
  noPad = false,
}: ModalProps) {
  const handleEscape = useCallback(
    (e: KeyboardEvent) => {
      if (e.key === 'Escape' && !loading) {
        onClose()
      }
    },
    [onClose, loading]
  )

  useEffect(() => {
    if (open) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = ''
    }
  }, [open, handleEscape])

  if (!open) return null

  return createPortal(
    <div
      className="fixed inset-0 z-[9999] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'modal-title' : undefined}
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={loading ? undefined : onClose}
        aria-hidden="true"
      />

      {/* Modal panel */}
      <div
        className={cn(
          'relative z-10 w-full glass-panel rounded-xl shadow-2xl flex flex-col max-h-[90vh] animate-fade-in',
          wide ? 'max-w-4xl' : 'max-w-2xl',
          className
        )}
      >
        {/* Header */}
        {title && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-mist dark:border-slate-700 flex-shrink-0">
            <h2
              id="modal-title"
              className="text-base font-semibold text-navy dark:text-slate-100"
            >
              {title}
            </h2>
            <button
              onClick={onClose}
              disabled={loading}
              className="p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-slate-700 dark:hover:text-slate-200 transition-colors disabled:opacity-50"
              aria-label="Close modal"
            >
              <X size={18} />
            </button>
          </div>
        )}

        {/* Body */}
        <div className={cn('flex-1 overflow-y-auto', !noPad && 'px-6 py-5', bodyClassName)}>
          {children}
        </div>

        {/* Footer */}
        {(!hideSave || extraActions) && (
          <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-mist dark:border-slate-700 flex-shrink-0">
            <div className="flex items-center gap-2">{extraActions}</div>
            <div className="flex items-center gap-2 ml-auto">
              <Button variant="secondary" onClick={onClose} disabled={loading}>
                Cancel
              </Button>
              {!hideSave && onSave && (
                <Button
                  variant={danger ? 'danger' : 'primary'}
                  onClick={onSave}
                  loading={loading}
                >
                  {saveLabel}
                </Button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}

// ===== Sub-components for composability =====

export function FormSection({
  title,
  description,
  children,
  className,
}: {
  title?: string
  description?: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('mb-6 last:mb-0', className)}>
      {(title || description) && (
        <div className="mb-4">
          {title && (
            <h3 className="text-sm font-semibold text-navy dark:text-slate-100">{title}</h3>
          )}
          {description && (
            <p className="text-xs text-steel dark:text-slate-400 mt-0.5">{description}</p>
          )}
        </div>
      )}
      {children}
    </div>
  )
}

export function FormGrid({
  children,
  cols = 2,
  className,
}: {
  children: React.ReactNode
  cols?: 1 | 2 | 3
  className?: string
}) {
  const colClass =
    cols === 1
      ? 'grid-cols-1'
      : cols === 3
      ? 'grid-cols-1 sm:grid-cols-3'
      : 'grid-cols-1 sm:grid-cols-2'
  return <div className={cn('grid gap-4', colClass, className)}>{children}</div>
}

export function Field({
  label,
  required,
  error,
  helper,
  children,
  className,
}: {
  label?: string
  required?: boolean
  error?: string
  helper?: string
  children: React.ReactNode
  className?: string
}) {
  return (
    <div className={cn('flex flex-col', className)}>
      {label && (
        <label className="form-label">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </label>
      )}
      {children}
      {error && <p className="mt-1 text-xs text-red-500">{error}</p>}
      {helper && !error && (
        <p className="mt-1 text-xs text-steel dark:text-slate-400">{helper}</p>
      )}
    </div>
  )
}

export default Modal
